import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './FinancialSummary.css';

const FinancialSummary = ({
  totalIncome = 0,
  totalExpenses = 0,
  netProfit = 0,
  takenLoans = 0,
  givenLoans = 0,
  loansPayable = 0,
  loansReceivable = 0,
  formatCurrency,
  onPeriodChange,
  onLoanDetailsClick,
  currentPeriod = 'week'
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [selectedPeriod, setSelectedPeriod] = useState(currentPeriod);

  useEffect(() => {
    setSelectedPeriod(currentPeriod);
  }, [currentPeriod]);

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    if (onPeriodChange) {
      onPeriodChange(period);
    }
  };

  const getPeriodDateRange = () => {
    const today = new Date();
    let startDate = new Date();

    switch(selectedPeriod) {
      case 'week':
        startDate.setDate(today.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(today.getMonth() - 1);
        break;
      case 'quarter':
        startDate.setMonth(today.getMonth() - 3);
        break;
      case 'year':
        startDate.setFullYear(today.getFullYear() - 1);
        break;
      default:
        startDate.setDate(today.getDate() - 7);
    }

    const formatDate = (date) => {
      return new Intl.DateTimeFormat(isRTL ? 'ar-EG' : 'en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }).format(date);
    };

    return `${t("From")} ${formatDate(startDate)} ${t("to")} ${formatDate(today)}`;
  };

  // Calculate loan balances
  // In Android: loansPayable comes from LOAN_REPAYMENT transactions
  // loansReceivable comes from REPAYMENT_RECEIVED transactions
  // If not available, fallback to taken/given loans
  const totalLoansPayable = loansPayable;
  const totalLoansReceivable = loansReceivable;

  return (
    <div className="financial-summary-container" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Period Selection Tabs */}
      <div className="period-selector-container">
        <div className="period-tabs">
          <button
            className={`period-tab ${selectedPeriod === 'week' ? 'active' : ''}`}
            onClick={() => handlePeriodChange('week')}
          >
            {t("Last Week")}
          </button>
          <button
            className={`period-tab ${selectedPeriod === 'month' ? 'active' : ''}`}
            onClick={() => handlePeriodChange('month')}
          >
            {t("Last Month")}
          </button>
          <button
            className={`period-tab ${selectedPeriod === 'quarter' ? 'active' : ''}`}
            onClick={() => handlePeriodChange('quarter')}
          >
            {t("Last Quarter")}
          </button>
          <button
            className={`period-tab ${selectedPeriod === 'year' ? 'active' : ''}`}
            onClick={() => handlePeriodChange('year')}
          >
            {t("Last Year")}
          </button>
        </div>
      </div>

      {/* Period Date Range */}
      <div className="period-date-display">
        <span>{getPeriodDateRange()}</span>
      </div>

      {/* Income Statement Section */}
      <div className="summary-section">
        <div className="section-label">{t("INCOME STATEMENT")}</div>

        <div className="summary-cards-grid">
          <div className="summary-value-card">
            <div className="value-title">{t("Net Profit")}</div>
            <div className={`value-number ${netProfit >= 0 ? 'profit' : 'loss'}`}>
              {netProfit >= 0 ? `+${formatCurrency(netProfit)}` : formatCurrency(netProfit)}
            </div>
            <div className="value-currency">{t("EGP")}</div>
          </div>

          <div className="summary-value-card double">
            <div className="value-group">
              <div className="value-title">{t("Income")}</div>
              <div className="value-number positive">+{formatCurrency(Math.abs(totalIncome))}</div>
              <div className="value-currency">{t("EGP")}</div>
            </div>
            <div className="value-group">
              <div className="value-title">{t("Expenses")}</div>
              <div className="value-number negative">-{formatCurrency(Math.abs(totalExpenses))}</div>
              <div className="value-currency">{t("EGP")}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Loans Section */}
      <div className="summary-section">
        <div className="section-header">
          <span className="section-label">{t("LOANS")}</span>
          {/* Hide See Details button for now
          {onLoanDetailsClick && (
            <button className="details-button" onClick={onLoanDetailsClick}>
              {t("See Details")}
            </button>
          )}
          */}
        </div>

        <div className="summary-cards-grid">
          <div className="summary-value-card">
            <div className="value-title">{t("Given Loans")}</div>
            <div className="value-number negative">-{formatCurrency(Math.abs(givenLoans))}</div>
            <div className="value-currency">{t("EGP")}</div>
          </div>

          <div className="summary-value-card">
            <div className="value-title">{t("Taken Loans")}</div>
            <div className="value-number positive">+{formatCurrency(Math.abs(takenLoans))}</div>
            <div className="value-currency">{t("EGP")}</div>
          </div>
        </div>
      </div>

      {/* Loan Balances Section */}
      <div className="summary-section">
        <div className="section-label">{t("LOAN BALANCES")}</div>

        <div className="loan-balance-box">
          <div className="balance-block">
            <h4 className="balance-title">{t("TOTAL LOANS PAYABLE")}</h4>
            <div className="balance-amount negative">
              -{formatCurrency(Math.abs(totalLoansPayable))}
            </div>
            <div className="balance-currency">{t("EGP")}</div>
            <p className="balance-description">
              {t("Outstanding balance of loans to be paid")}
            </p>
          </div>

          <div className="balance-divider" />

          <div className="balance-block">
            <h4 className="balance-title">{t("TOTAL LOANS RECEIVABLE")}</h4>
            <div className="balance-amount positive">
              +{formatCurrency(Math.abs(totalLoansReceivable))}
            </div>
            <div className="balance-currency">{t("EGP")}</div>
            <p className="balance-description">
              {t("Outstanding balance of loans to be received")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialSummary;